import { Field, Formik, FormikErrors } from 'formik';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroRichText,
	CerebroText,
	CerebroYesNo,
} from '../../shared/components';
import { LenderFeedbackSummary } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { Grid } from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { getApiResponseData } from '../../shared/utils/axios';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { QueryKeys } from '../../shared/hooks';

interface LenderSummaryReportFormProps {
	dataRoomGuid: string | undefined;
	report: LenderFeedbackSummary | null;
	close: () => void;
}

export const LenderSummaryReportForm: React.FC<LenderSummaryReportFormProps> = ({ dataRoomGuid, report, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const saveMutation = useMutation(
		async ({ values }: LenderSummaryReportFormMutationVariables) => {
			const dto = {
				bankDealProfileId: report?.bankDealProfileId,
				lenderFeedbackSummaryId: report?.lenderFeedbackSummaryId,
				feedbackText: values.feedbackText,
				nextStepsText: values.nextStepsText,
				isVisibleToBorrower: values.isVisibleToBorrower,
				isDoneEditing: values.isDoneEditing,
			};

			const { data, error } = await getApiResponseData<LenderFeedbackSummary>(
				api.post(CerebroApiEndpoints.DataRooms.LenderSummaryReport(dataRoomGuid), dto)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Lender summary was updated.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				client.invalidateQueries(QueryKeys.DataRoom.LenderSummaryReport(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Root(dataRoomGuid));
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={report != null} onClose={() => close()} PaperProps={{ sx: { width: '700px' } }}>
			<CerebroHeading>Edit Feedback Summary</CerebroHeading>
			<CerebroHeading size="h3" component="h3">
				{report?.bankName}
			</CerebroHeading>

			<Formik
				initialValues={{
					feedbackText: report?.feedbackTextDisplay,
					nextStepsText: report?.nextStepsTextDisplay,
					isVisibleToBorrower: report?.isVisibleToBorrower ?? true,
					isDoneEditing: false,
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(saveMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm flatIndicator onSubmit={handleSubmit} sx={{ marginTop: 3, paddingRight: 3 }}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel id="feedbackText" required>
										Feedback
									</CerebroLabel>
									<CerebroRichText
										value={values.feedbackText ?? ''}
										onChange={updatedValue => setFieldValue('feedbackText', updatedValue)}
									/>
									{FormikUtils.IsInvalid('feedbackText', errors, touched) && (
										<CerebroText variant="error">{errors.feedbackText}</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel id="nextStepsText" required>
										Re-Engage Point
									</CerebroLabel>
									<CerebroRichText
										value={values.nextStepsText ?? ''}
										onChange={updatedValue => setFieldValue('nextStepsText', updatedValue)}
									/>
									{FormikUtils.IsInvalid('nextStepsText', errors, touched) && (
										<CerebroText variant="error">{errors.nextStepsText}</CerebroText>
									)}
								</Grid>
								<Grid item xs={12}>
									<CerebroLabel id="isVisibleToBorrower">Visible to Borrower</CerebroLabel>
									<Field
										id="isVisibleToBorrower"
										as={CerebroYesNo}
										name="isVisibleToBorrower"
										disabled={isSubmitting}
									/>
									<CerebroText variant="hint">
										If unchecked, the row will no longer be shown to the borrower when viewing the
										report.
									</CerebroText>
								</Grid>
								<Grid item xs={11}>
									<CerebroLabel id="isDoneEditing">Done Editing Feedback Summary?</CerebroLabel>
									<Field
										id="isDoneEditing"
										as={CerebroYesNo}
										name="isDoneEditing"
										disabled={isSubmitting}
									/>
									<CerebroText variant="hint">
										Set to Yes if this is the last change you are making and the data room setting
										will be updated to turn off edit mode for this page.
									</CerebroText>
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type LenderSummaryReportFormMutationVariables = FormikUtils.MutateOnSubmitVariables<LenderSummaryReportFormValues>;
interface LenderSummaryReportFormValues {
	feedbackText: string | undefined;
	nextStepsText: string | undefined;
	isVisibleToBorrower: boolean;
	isDoneEditing: boolean;
}
type CovenantFormValueErrors = FormikErrors<LenderSummaryReportFormValues>;

function validateForm(values: LenderSummaryReportFormValues) {
	const errors: CovenantFormValueErrors = {};

	if (!values.feedbackText) {
		errors.feedbackText = 'You must enter feedback details.';
	}

	if (values.isVisibleToBorrower) {
		if (!values.nextStepsText) {
			errors.nextStepsText = 'You must enter re-engage point details.';
		}
	}

	return errors;
}
